import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import "../../css/ReleaseNotes.css";
import { BetaBadge } from "../common/ui";
import { RichText, formatDate, imageSrc, splitBeta, tagFor } from "./changelogShared";

const OPEN_BY_DEFAULT = 3;

const slug = version => version.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// One release item. A tag is shown when the type/label changes from the previous item,
// so a run of items reads as one "Features" / "Enhancement" / "Bug Fixes" group.
export function ReleaseItem({ item, version, showTag }) {
  const tag = tagFor(item);
  const points = (item.points || []).filter(Boolean);
  const src = imageSrc(item);
  const title = splitBeta(item.title);
  return (
    <section className="cl-item">
      {showTag && <span className={`cl-tag ${tag.className}`}>{tag.text}</span>}
      {item.title && (
        <h3 className="cl-item__title">
          {title.text}
          {title.beta && <BetaBadge />}
        </h3>
      )}
      {points.length > 0 && (
        <div className="cl-item__text">
          {points.length === 1 ? (
            <p>
              <RichText text={points[0]} />
            </p>
          ) : (
            <ul>
              {points.map((p, i) => (
                <li key={i}>
                  <RichText text={p} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {src && (
        <figure className="cl-item__shot">
          <img src={src} alt={`${item.title || "Screenshot"} in Froth TestOps ${version}`} loading="lazy" decoding="async" />
        </figure>
      )}
    </section>
  );
}

export const tagKey = item => `${item.type}|${item.label || ""}`;

// Linear-style change log: a sticky version rail on the left, the release on the right.
// The newest releases are expanded; older ones open on demand.
export default function ReleaseNotes({ releases = [], openAll = false }) {
  // Only explicit toggles are stored, so newly loaded releases still get the default.
  const [toggled, setToggled] = useState({});

  const toggle = (version, isOpen) => setToggled(prev => ({ ...prev, [version]: !isOpen }));

  return (
    <div className="cl">
      {releases.map((release, index) => {
        const isOpen = openAll || (toggled[release.version] ?? index < OPEN_BY_DEFAULT);
        const id = slug(release.version) || `release-${index}`;
        const date = formatDate(release.date);
        return (
          <article className="cl-entry" id={id} key={release.id || release.version || index} aria-labelledby={`${id}-title`}>
            <div className="cl-entry__rail">
              <span className="cl-entry__dot" aria-hidden="true" />
              <h2 className="cl-entry__version" id={`${id}-title`}>
                {release.version}
              </h2>
              {index === 0 && <span className="cl-entry__latest">Latest</span>}
              {date && <time className="cl-entry__date" dateTime={release.date}>{date}</time>}
            </div>

            <div className="cl-entry__body">
              {(release.description || []).filter(Boolean).map((d, i) => (
                <p className="cl-entry__lead" key={i}>
                  <RichText text={d} />
                </p>
              ))}

              {isOpen && (
                <div id={`${id}-details`}>
                  {(release.details || []).map((item, i, all) => (
                    <ReleaseItem
                      key={i}
                      item={item}
                      version={release.version}
                      showTag={i === 0 || tagKey(all[i - 1]) !== tagKey(item)}
                    />
                  ))}
                </div>
              )}

              {!openAll && (release.details || []).length > 0 && (
                <button
                  type="button"
                  className="cl-toggle"
                  aria-expanded={isOpen}
                  aria-controls={`${id}-details`}
                  onClick={() => toggle(release.version, isOpen)}
                >
                  {isOpen ? "Hide details" : `See what's in ${release.version}`}
                  <ChevronDown size={16} aria-hidden="true" />
                </button>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}
